/* Batch photoshoot handoff from photoshoot.bazaart.com → design.bazaart.com */
import { openDB } from 'idb';
import type { AspectRatioKey } from '@/scenes/Editor/components/FloatingInspectorButton/SharedComponents/Popover/ratioOptions';
import { IndexedBlobLike, toReadableBlob, toStorableBlobPayload } from '@/utils/indexedBlob';

/** Must stay in sync with `public/bridge.html`. */
export const IMAGE_DB_NAME = 'ImageDB';
export const IMAGE_DB_VERSION = 4;
export const PHOTOSHOOT_TRANSFERS_STORE = 'photoshootTransfers';

/** Query param on `/create/photoshoots` after redirect from photoshoot.bazaart.com. */
export const PHOTOSHOOT_TRANSFER_QUERY_PARAM = 'photoshootTransfer';

/**
 * Alternative to `photoshootTransfer=<id>`: when `from=photoshoot` is present
 * (forwarded by the `/sso` route), the design app loads the most recent stored
 * transfer instead of one addressed by id.
 */
export const PHOTOSHOOT_SOURCE_QUERY_PARAM = 'from';
export const PHOTOSHOOT_SOURCE_VALUE = 'photoshoot';

export const PHOTOSHOOT_RESULT_COUNT = 4;
export const PHOTOSHOOT_ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const PHOTOSHOOT_MAX_BLOB_BYTES = 10 * 1024 * 1024;
export const PHOTOSHOOT_MAX_BATCH_BYTES = 50 * 1024 * 1024;
export const VALID_PHOTOSHOOT_ASPECT_RATIOS = new Set<AspectRatioKey>([
  'Square',
  '4:5',
  '3:4',
  '2:3',
  '9:16',
]);

export interface PhotoshootTransferRecord {
  id: string;
  sourceBlob: Blob;
  resultBlobs: Blob[];
  labels?: string[];
  aspectRatio?: AspectRatioKey;
  ts: number;
}

interface RawPhotoshootTransferRecord {
  id: string;
  sourceBlob: IndexedBlobLike;
  sourceBlobType?: string;
  resultBlobs: IndexedBlobLike[];
  resultBlobTypes?: string[];
  labels?: string[];
  aspectRatio?: AspectRatioKey;
  ts: number;
}

export interface PhotoshootPrefilledRun {
  sourceUrl: string;
  resultUrls: string[];
  labels: string[];
  aspectRatio?: AspectRatioKey;
}

export interface SavePhotoshootTransferPayload {
  transferId: string;
  sourceBlob: Blob;
  resultBlobs: Blob[];
  labels?: string[];
  aspectRatio?: AspectRatioKey;
  ts?: number;
}

async function openPhotoshootDB() {
  return openDB(IMAGE_DB_NAME, IMAGE_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('images')) {
        db.createObjectStore('images', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PHOTOSHOOT_TRANSFERS_STORE)) {
        db.createObjectStore(PHOTOSHOOT_TRANSFERS_STORE, { keyPath: 'id' });
      }
    },
  });
}

function normalizeTransferRecord(
  record: RawPhotoshootTransferRecord | null | undefined,
): PhotoshootTransferRecord | null {
  if (!record) return null;

  const sourceBlob = toReadableBlob(record.sourceBlob, record.sourceBlobType);
  if (!sourceBlob) return null;

  const normalizedResultBlobs: Blob[] = [];
  for (let i = 0; i < (record.resultBlobs?.length ?? 0); i++) {
    const normalized = toReadableBlob(record.resultBlobs[i], record.resultBlobTypes?.[i]);
    if (!normalized) return null;
    normalizedResultBlobs.push(normalized);
  }

  return {
    id: record.id,
    sourceBlob,
    resultBlobs: normalizedResultBlobs,
    labels: record.labels,
    aspectRatio: record.aspectRatio,
    ts: record.ts,
  };
}

export async function loadPhotoshootTransfer(
  transferId: string,
): Promise<PhotoshootTransferRecord | null> {
  const db = await openPhotoshootDB();
  const rec = (await db.get(PHOTOSHOOT_TRANSFERS_STORE, transferId)) as
    | RawPhotoshootTransferRecord
    | undefined;
  await db.close();
  return normalizeTransferRecord(rec);
}

export async function hasPhotoshootTransfer(transferId: string): Promise<boolean> {
  const db = await openPhotoshootDB();
  try {
    const rec = await db.get(PHOTOSHOOT_TRANSFERS_STORE, transferId);
    return Boolean(rec);
  } finally {
    await db.close();
  }
}

export async function savePhotoshootTransfer(
  payload: SavePhotoshootTransferPayload,
): Promise<void> {
  const sourceStorable = await toStorableBlobPayload(payload.sourceBlob);
  const resultStorables = await Promise.all(
    payload.resultBlobs.map(blob => toStorableBlobPayload(blob)),
  );

  const db = await openPhotoshootDB();
  try {
    await db.put(PHOTOSHOOT_TRANSFERS_STORE, {
      id: payload.transferId,
      sourceBlob: sourceStorable.value,
      sourceBlobType: sourceStorable.type,
      resultBlobs: resultStorables.map(item => item.value),
      resultBlobTypes: resultStorables.map(item => item.type),
      labels: payload.labels,
      aspectRatio: payload.aspectRatio,
      ts: payload.ts ?? Date.now(),
    });
  } finally {
    await db.close();
  }
}

export async function deletePhotoshootTransfer(transferId: string): Promise<void> {
  const db = await openPhotoshootDB();
  await db.delete(PHOTOSHOOT_TRANSFERS_STORE, transferId);
  await db.close();
}

/**
 * Returns the most recently stored transfer (max `ts`). Used by the
 * `from=photoshoot` path where no transfer id is supplied. Single O(n) pass to
 * avoid sorting the whole store.
 */
export async function loadLatestPhotoshootTransfer(): Promise<PhotoshootTransferRecord | null> {
  const db = await openPhotoshootDB();
  try {
    const all = (await db.getAll(PHOTOSHOOT_TRANSFERS_STORE)) as RawPhotoshootTransferRecord[];
    if (!all.length) return null;
    let latest: PhotoshootTransferRecord | null = null;
    for (let i = 0; i < all.length; i++) {
      const normalized = normalizeTransferRecord(all[i]);
      if (!normalized) continue;
      if (!latest || normalized.ts > latest.ts) latest = normalized;
    }
    return latest;
  } finally {
    await db.close();
  }
}

/**
 * Drains the transfer store. The `from=photoshoot` path has no id to scope a
 * delete to, so it clears everything after consuming the latest record to keep
 * stale transfers from accumulating.
 */
export async function clearPhotoshootTransfers(): Promise<void> {
  const db = await openPhotoshootDB();
  try {
    await db.clear(PHOTOSHOOT_TRANSFERS_STORE);
  } finally {
    await db.close();
  }
}

/** Converts a stored transfer into blob URLs for the photoshoot run UI. */
export function hydratePhotoshootTransfer(
  record: PhotoshootTransferRecord,
  fallbackLabels: readonly string[],
): PhotoshootPrefilledRun {
  const resultUrls = record.resultBlobs.map(blob => URL.createObjectURL(blob));
  const labels = fallbackLabels.map((fallback, index) => record.labels?.[index] ?? fallback);

  return {
    sourceUrl: URL.createObjectURL(record.sourceBlob),
    resultUrls,
    labels,
    aspectRatio: record.aspectRatio,
  };
}

export function revokePhotoshootPrefilledUrls(prefilled: PhotoshootPrefilledRun): void {
  if (prefilled.sourceUrl.startsWith('blob:')) {
    URL.revokeObjectURL(prefilled.sourceUrl);
  }
  revokePhotoshootPrefilledResultUrls(prefilled);
}

/** Revokes generated result preview URLs but keeps the source URL intact. */
export function revokePhotoshootPrefilledResultUrls(prefilled: PhotoshootPrefilledRun): void {
  prefilled.resultUrls.forEach(url => {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  });
}
